import { Dialog, DialogTitle, DialogContent, Stack, Divider, Alert } from '@mui/material';
import { FormattedDate, FormattedMessage } from 'react-intl';

import { useMemo } from 'react';
import {
  SUBSCRIPTION_CONFIG,
  calculateRemainingDays,
  calculateCredit,
  calculateBonusDays,
  DAYS_PER_MONTH,
  getFeatureDifference,
  SubscriptionChangeDialogProps,
  SubscriptionPlansEnum,
  SubscriptionPlans,
} from '../features/subscription/entity';
import BonusDaysInfo from '../features/subscription/components/BonusDaysInfo';
import DialogActionsButtons from '../features/subscription/components/DialogActionsButtons';
import FeaturesChangeSection from '../features/subscription/components/FeaturesChangeSection';
import PaymentInfo from '../features/subscription/components/PaymentInfo';
import PlanInfo from '../features/subscription/components/PlanInfo';

const useSubscriptionChangeCalculations = (
  currentPlan: SubscriptionPlans,
  newPlan: SubscriptionPlans,
  endDate: string,
  currency: 'CZK' | 'EUR' = 'CZK',
) => {
  return useMemo(() => {
    const currentConfig = SUBSCRIPTION_CONFIG[currentPlan];
    const newConfig = SUBSCRIPTION_CONFIG[newPlan];

    const isUpgrade =
      currentPlan === SubscriptionPlansEnum.PRO && newPlan === SubscriptionPlansEnum.EXPERT;
    const isDowngrade =
      currentPlan === SubscriptionPlansEnum.EXPERT && newPlan === SubscriptionPlansEnum.PRO;

    const currentDailyRate =
      currency === 'EUR' ? currentConfig.dailyRateEur : currentConfig.dailyRate;
    const newDailyRate = currency === 'EUR' ? newConfig.dailyRateEur : newConfig.dailyRate;
    const currentPrice = currency === 'EUR' ? currentConfig.priceEur : currentConfig.price;
    const newPrice = currency === 'EUR' ? newConfig.priceEur : newConfig.price;

    const remainingDays = calculateRemainingDays(endDate);
    const credit = calculateCredit(currentDailyRate, remainingDays);
    const bonusDays = calculateBonusDays(credit, newDailyRate);
    const totalDays = DAYS_PER_MONTH + bonusDays;

    const gainedFeatures = isUpgrade
      ? getFeatureDifference(currentConfig.features, newConfig.features)
      : [];

    const lostFeatures = isDowngrade
      ? getFeatureDifference(newConfig.features, currentConfig.features)
      : [];

    return {
      currentConfig,
      newConfig,
      currentPrice,
      newPrice,
      isUpgrade,
      isDowngrade,
      remainingDays,
      creditInCzk: credit,
      bonusDays,
      totalDays,
      gainedFeatures,
      lostFeatures,
    };
  }, [currentPlan, newPlan, endDate, currency]);
};

export const SubscriptionChangeDialog = ({
  open,
  onClose,
  onConfirm,
  currentPlan,
  newPlan,
  endDate,
  currency = 'CZK',
  isTrial = false,
}: SubscriptionChangeDialogProps) => {
  const {
    currentConfig,
    newConfig,
    currentPrice,
    newPrice,
    remainingDays,
    creditInCzk,
    bonusDays,
    totalDays,
    gainedFeatures,
    lostFeatures,
  } = useSubscriptionChangeCalculations(currentPlan, newPlan, endDate, currency);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage id="subscriptionChangeDialog.title" defaultMessage="Změna předplatného" />
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <PlanInfo
            label={
              <FormattedMessage
                id="subscriptionChangeDialog.currentPlan"
                defaultMessage="Aktuální tarif: "
              />
            }
            planName={currentConfig.name}
            price={currentPrice}
            color="primary"
            currency={currency}
          />
          <Divider />
          <PlanInfo
            label={
              <FormattedMessage id="subscriptionChangeDialog.newPlan" defaultMessage="Nový tarif" />
            }
            planName={newConfig.name}
            price={newPrice}
            color="success.main"
            currency={currency}
          />
          <FeaturesChangeSection features={gainedFeatures} type="gained" />
          <FeaturesChangeSection features={lostFeatures} type="lost" />
          {isTrial ? (
            <Alert severity="info">
              <FormattedMessage
                id="subscriptionChangeDialog.trialChange"
                defaultMessage="Tarif se změní ihned bez platby. Zkušební období končí {date}. Cena nového tarifu se při automatickém obnovení účtuje až po skončení zkušebního období."
                values={{ date: <FormattedDate value={endDate} /> }}
              />
            </Alert>
          ) : (
            <>
              <BonusDaysInfo
                remainingDays={remainingDays}
                creditInCzk={creditInCzk}
                bonusDays={bonusDays}
                totalDays={totalDays}
                currency={currency}
              />
              <PaymentInfo newPrice={newPrice} currency={currency} />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActionsButtons isTrial={isTrial} onClose={onClose} onConfirm={onConfirm} />
    </Dialog>
  );
};
